/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, useAppStore } from '@/store';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireOwner?: boolean;
  /**
   * When set alongside requireAdmin, partners are also allowed through — used for
   * admin pages that partners may access to manage their own owners (e.g. Settings).
   */
  allowPartner?: boolean;
  /**
   * GUI page key (see DEFAULT_GUI_PAGES). When set and the owner has closed this
   * page in Settings, the route is blocked and redirects to the dashboard — so a
   * closed page can't be reached by URL or a stray link, not just hidden from menus.
   * Admins are never blocked.
   */
  guiKey?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireOwner = false,
  allowPartner = false,
  guiKey,
}) => {
  const { isAuthenticated, isAdmin, isOwner, isPartner } = useAuthStore();
  const { guiSettings } = useAppStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin && !(allowPartner && isPartner)) {
    return <Navigate to="/" replace />;
  }

  if (requireOwner && !isOwner && !isAdmin && !(allowPartner && isPartner)) {
    return <Navigate to="/" replace />;
  }

  // Page-visibility gate: a closed page is unreachable for non-admins.
  // (guiSettings is null until loaded — treat unknown as visible to avoid a redirect flash.)
  if (guiKey && !isAdmin && guiSettings?.pages && guiSettings.pages[guiKey]?.visible === false) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
